/**
 * The Directory (prototype `election-directory`): an editorial intro, the
 * featured current cycle beside up to three upcoming ones, then the complete
 * ledger grouped by year with search, year, status and jurisdiction filters.
 * Filters live in the URL and are written with replaceState.
 */
import type { PublicCycleSummary } from "../api";
import type { ViewRenderer } from "../context";
import { h, heading } from "../dom";
import { electionHref } from "../links";
import {
  cycleDate,
  cycleStatus,
  cycleYears,
  dateParts,
  featuredAndUpcoming,
  filterCycles,
  formatBallotDates,
  groupByYear,
  jurisdictionLabel,
  localToday,
  normalizeDirectoryFilters,
} from "../model";
import { buildUrl, CYCLE_STATUSES, type CycleStatus, type DirectoryFilters, type View } from "../router";
import { renderState } from "../components/states";
import { singleSelect } from "../components/dropdown";

const STATUS_LABEL: Record<CycleStatus, string> = {
  current: "Current election",
  upcoming: "Upcoming",
  past: "Past election",
};

type Placement = "featured" | "upcoming" | "row";

function lifecycle(status: CycleStatus) {
  return h("span", { class: `bsc-lifecycle bsc-lifecycle--${status}` }, STATUS_LABEL[status]);
}

function dateBlock(iso: string) {
  if (!iso) return null;
  const p = dateParts(iso);
  return h(
    "time",
    { class: "bsc-cycle__date", datetime: iso },
    h("span", null, p.month),
    " ",
    h("strong", null, p.day),
    " ",
    h("span", null, p.year)
  );
}

function cycleCard(cycle: PublicCycleSummary, placement: Placement, page: URL, today: string, level: number) {
  const status = cycleStatus(cycle, today);
  const href = electionHref(page, cycle);
  const jurisdictions = cycle.jurisdictions.map(jurisdictionLabel).join(" · ");
  const ballots = formatBallotDates(cycle.ballots);
  return h(
    "article",
    { class: `bsc-cycle bsc-cycle--${placement}`, "data-status": status, "data-cycle-slug": cycle.slug },
    dateBlock(cycleDate(cycle, today)),
    h(
      "div",
      { class: "bsc-cycle__body" },
      lifecycle(status),
      heading(level, { class: "bsc-cycle__name" }, cycle.name),
      ballots ? h("p", { class: "bsc-cycle__ballots" }, ballots) : null,
      jurisdictions ? h("p", { class: "bsc-cycle__jurisdiction" }, jurisdictions) : null
    ),
    h(
      "a",
      { class: "bsc-cycle__link", href },
      placement === "upcoming" ? h("span", { class: "bsc-sr-only" }, `Explore ${cycle.name}`) : "Explore election",
      h("span", { "aria-hidden": "true" }, " →")
    )
  );
}

function intro() {
  return h(
    "header",
    { class: "bsc-directory__intro" },
    h("p", { class: "bsc-directory__eyebrow" }, "Resources · Elections"),
    h("h1", null, "Elections"),
    h("p", { class: "bsc-directory__summary-copy" }, "Follow the current election cycle and find every election in the archive.")
  );
}

export const renderDirectory: ViewRenderer<Extract<View, { kind: "directory" }>> = async (region, view, ctx) => {
  const root = h("div", { class: "bsc-directory" }, intro());
  const body = h("div", { class: "bsc-directory__body" });
  root.append(body);
  region.replaceChildren(root);
  renderState("loading", body, { subject: "directory" });

  const result = await ctx.api.getCycles();
  if (!result.ok) {
    renderState("unavailable", body, { subject: "directory", onRetry: () => ctx.rerender() });
    return "All elections";
  }
  const cycles = result.data;
  if (cycles.length === 0) {
    renderState("empty", body, { subject: "directory" });
    return "All elections";
  }

  const today = localToday();
  const page = ctx.pageUrl();
  const normalized = normalizeDirectoryFilters(cycles, view.filters);
  const filters: DirectoryFilters = normalized.filters;
  const knownJurisdictions = [...new Set(cycles.flatMap((c) => c.jurisdictions))].sort();
  const years = cycleYears(cycles);
  if (normalized.changed) {
    ctx.replace(buildUrl({ kind: "directory", filters }, page));
  }

  // ---- Featured + upcoming -------------------------------------------------
  const { featured, upcoming } = featuredAndUpcoming(cycles, today);
  const stage = featured
    ? h(
        "div",
        { class: "bsc-directory__stage" },
        h("section", { class: "bsc-directory__featured", "aria-label": "Featured election" }, cycleCard(featured, "featured", page, today, 2)),
        upcoming.length
          ? h(
              "section",
              { class: "bsc-directory__upcoming", "aria-labelledby": "bsc-upcoming-title" },
              h("h2", { id: "bsc-upcoming-title" }, "Upcoming elections"),
              upcoming.map((c) => cycleCard(c, "upcoming", page, today, 3))
            )
          : null
      )
    : null;

  // ---- Ledger ----------------------------------------------------------------
  const results = h("div", { class: "bsc-directory__results" });
  const count = h("p", { class: "bsc-directory__count", "aria-live": "polite" });

  function writeUrl() {
    ctx.replace(buildUrl({ kind: "directory", filters }, ctx.pageUrl()));
  }

  function drawResults() {
    const matches = filterCycles(cycles, filters, today);
    count.replaceChildren(h("strong", null, String(matches.length)), ` of ${cycles.length} elections`);
    if (matches.length === 0) {
      results.replaceChildren(
        h(
          "div",
          { class: "bsc-directory__empty" },
          h("h3", null, "No elections match these filters."),
          h("p", null, "Change the search or reset the filters to see every election."),
          h("button", { type: "button", class: "bsc-filters__reset", onClick: reset }, "Reset filters")
        )
      );
      return;
    }
    results.replaceChildren(
      ...[...groupByYear(matches, today)].map(([year, list]) =>
        h(
          "section",
          { class: "bsc-directory__year", "aria-labelledby": `bsc-year-${year}` },
          h("h3", { class: "bsc-directory__year-title", id: `bsc-year-${year}` }, year),
          list.map((c) => cycleCard(c, "row", page, today, 4))
        )
      )
    );
  }

  const search = h("input", {
    type: "search",
    id: "bsc-directory-search",
    placeholder: "Election or jurisdiction",
    autocomplete: "off",
    value: filters.q,
    onInput: (e: Event) => {
      filters.q = (e.target as HTMLInputElement).value;
      writeUrl();
      drawResults();
    },
  });

  const pills = h(
    "div",
    { class: "bsc-filters__pills", role: "group", "aria-label": "Election status" },
    (["all", ...CYCLE_STATUSES] as const).map((value) =>
      h(
        "button",
        {
          type: "button",
          class: "bsc-filters__pill",
          "data-status-filter": value,
          "aria-pressed": String((filters.status ?? "all") === value),
          onClick: (e: Event) => {
            filters.status = value === "all" ? null : value;
            pills.querySelectorAll("button").forEach((b) => b.setAttribute("aria-pressed", String(b === e.currentTarget)));
            writeUrl();
            drawResults();
          },
        },
        value === "all" ? "All" : STATUS_LABEL[value]
      )
    )
  );

  const yearControl = () =>
    singleSelect({
      name: "year",
      label: "Year",
      options: [{ value: "", label: "All years" }, ...years.map((y) => ({ value: y, label: y }))],
      value: filters.year ?? "",
      onChange: (v) => {
        filters.year = v || null;
        writeUrl();
        drawResults();
      },
    });
  const jurisdictionControl = () =>
    singleSelect({
      name: "jurisdiction",
      label: "Jurisdiction",
      options: [
        { value: "", label: "All jurisdictions" },
        ...knownJurisdictions.map((j) => ({ value: j, label: jurisdictionLabel(j) })),
      ],
      value: filters.jurisdiction ?? "",
      onChange: (v) => {
        filters.jurisdiction = v || null;
        writeUrl();
        drawResults();
      },
    });

  const secondary = h(
    "div",
    { class: "bsc-filters__secondary", id: "bsc-directory-filters" },
    yearControl(),
    h("div", { class: "bsc-filters__status" }, h("span", { class: "bsc-filters__label" }, "Status"), pills),
    jurisdictionControl()
  );
  const mobileToggle = h(
    "button",
    {
      type: "button",
      class: "bsc-filters__toggle",
      "aria-expanded": "false",
      "aria-controls": "bsc-directory-filters",
      onClick: () => {
        const open = mobileToggle.getAttribute("aria-expanded") !== "true";
        mobileToggle.setAttribute("aria-expanded", String(open));
        secondary.classList.toggle("is-open", open);
      },
    },
    "Filter elections"
  );

  function reset() {
    filters.q = "";
    filters.year = null;
    filters.status = null;
    filters.jurisdiction = null;
    search.value = "";
    pills.querySelectorAll("button").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.statusFilter === "all")));
    secondary.firstElementChild!.replaceWith(yearControl());
    secondary.lastElementChild!.replaceWith(jurisdictionControl());
    writeUrl();
    drawResults();
    search.focus();
  }

  const ledger = h(
    "section",
    { class: "bsc-directory__ledger", "aria-labelledby": "bsc-ledger-title" },
    h("h2", { id: "bsc-ledger-title", class: "bsc-directory__ledger-title" }, "All elections"),
    h(
      "div",
      { class: "bsc-filters", role: "search" },
      h("label", { class: "bsc-filters__search", for: "bsc-directory-search" }, h("span", { class: "bsc-filters__label" }, "Search elections"), search),
      mobileToggle,
      secondary,
      h(
        "div",
        { class: "bsc-directory__summary" },
        count,
        h("button", { type: "button", class: "bsc-filters__reset", onClick: reset }, "Reset")
      )
    ),
    results
  );

  drawResults();
  body.replaceChildren(...[stage, ledger].filter((n): n is HTMLElement => n !== null));
  return "All elections";
};
